import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { dbGetAll } from '../../lib/printFileReview/db';
import { subscribeReviewStore } from '../../lib/printFileReview/events';
import { areRetriesStopped } from '../../lib/printFileReview/sync';
import { ILocalBatch, ILocalFile, IOutboxOp } from '../../lib/printFileReview/types';

interface IReviewData {
    loading: boolean;
    error: string | null;
    batches: ILocalBatch[];
    files: ILocalFile[];
    outbox: IOutboxOp[];
    retriesStopped: boolean;
}

/** Live view of the review DB (metadata only — images are read on demand). */
export const usePrintReviewData = () => {
    const [state, setState] = useState<IReviewData>({ loading: true, error: null, batches: [], files: [], outbox: [], retriesStopped: areRetriesStopped() });
    const mounted = useRef(true);

    const reload = useCallback(async () => {
        try {
            const [batches, files, outbox] = await Promise.all([
                dbGetAll<ILocalBatch>('batches'),
                dbGetAll<ILocalFile>('files'),
                dbGetAll<IOutboxOp>('outbox'),
            ]);
            if (!mounted.current) return;
            setState({
                loading: false,
                error: null,
                batches: batches.sort((a, b) => b.createdAt - a.createdAt),
                files,
                outbox,
                retriesStopped: areRetriesStopped(),
            });
        } catch (e: any) {
            if (!mounted.current) return;
            setState((s) => ({ ...s, loading: false, error: e?.message || 'Local database is unavailable' }));
        }
    }, []);

    useEffect(() => {
        mounted.current = true;
        void reload();
        const unsubscribe = subscribeReviewStore(() => void reload());
        return () => {
            mounted.current = false;
            unsubscribe();
        };
    }, [reload]);

    const filesByBatch = useMemo(() => {
        const map: Record<string, ILocalFile[]> = {};
        state.files.forEach((f) => {
            if (!map[f.batchId]) map[f.batchId] = [];
            map[f.batchId].push(f);
        });
        Object.values(map).forEach((list) => list.sort((a, b) => a.index - b.index));
        return map;
    }, [state.files]);

    return { ...state, filesByBatch, reload };
};
